import { supabase } from '../lib/supabase.js';
import { calcEventBudget } from '../utils/vypocet-mezd.js';
import { openNewEventModal } from '../components/new-event-modal.js';

let settingsCache = null;
async function getSettings() {
    if (settingsCache) return settingsCache;
    const { data } = await supabase.from('settings').select('key, value');
    settingsCache = Object.fromEntries((data || []).map(s => [s.key, s.value]));
    return settingsCache;
}

function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtKc(n) {
    return new Intl.NumberFormat('cs-CZ').format(Math.round(n || 0)) + ' Kč';
}

function fmtDate(d) {
    const dt = new Date(d);
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}

function fmtBlockDate(b) {
    if (b.event_date_end && b.event_date_end !== b.event_date) {
        return `${fmtDate(b.event_date)} – ${fmtDate(b.event_date_end)}`;
    }
    return fmtDate(b.event_date);
}

function calcEvent(ev, settings) {
    const musicians = (ev.musicians || []).map(em => ({
        rowId: em.id,
        musicianId: em.musician_id,
        name: em.profile?.name || '?',
        role: em.profile?.musician_role,
        km: em.km || 0,
        share: em.share_percent || em.profile?.share_percent || 100
    }));
    const budget = calcEventBudget({
        price:         Number(ev.price_total)     || 0,
        equipmentRent: Number(ev.equipment_rent)  || 0,
        equipmentKm:   Number(ev.equipment_km)    || 0,
        foodAllowance: Number(ev.food_allowance)  || 0,
        vanRate:       Number(ev.rate_van_per_km) || Number(settings.rate_van_per_km) || 12,
        carRate:       Number(ev.rate_car_per_km) || Number(settings.rate_car_per_km) || 8,
        targetBandWage: Number(ev.target_band_wage) || Number(settings.target_band_wage) || 500,
        roundingStep:   Number(ev.rounding_step)    || Number(settings.rounding_step)    || 100,
        musicians: musicians.map(m => ({ id: m.rowId, share: m.share, km: m.km }))
    });
    return { event: ev, musicians, budget };
}

function renderChips(musicians, budget, eventId) {
    const chips = musicians.map(m => {
        const w = budget.wages.find(x => x.id === m.rowId);
        const isHost = m.role === 'stalý host';
        return `<span class="muz-chip${isHost ? ' host' : ''}" data-row="${m.rowId}" data-share="${m.share}" data-km="${m.km}" data-name="${esc(m.name)}" oncontextmenu="event.preventDefault(); this.querySelector('.chip-remove').click();" title="Klikni pro úpravu km/podílu, pravé tlačítko odebere">
            <span class="name">${esc(m.name)}${isHost ? ` <span class="host-tag">${m.share}%</span>` : ''}</span>
            ${m.km > 0 ? `<span class="km">+${m.km}km</span>` : ''}
            <span class="sep">·</span>
            <span class="pay">${fmtKc(w?.payout || 0)}</span>
            <button type="button" class="chip-remove" data-row="${m.rowId}" data-name="${esc(m.name)}" title="Odebrat z akce">×</button>
        </span>`;
    }).join('');
    const addBtn = `<button type="button" class="muz-chip-add" data-event="${eventId}" title="Přidat muzikanta z databáze">+ Muzikant</button>`;
    return chips + addBtn;
}

function renderRow(calced) {
    const { event: ev, musicians, budget } = calced;
    const isPending = ev.status === 'pending';
    return `
        <tr data-event="${ev.id}"${isPending ? ' class="row-pending"' : ''}>
            <td class="date-col editable-cell" data-edit="date" data-event="${ev.id}" data-value="${ev.event_date}" title="Dvojklik = upravit datum">${fmtDate(ev.event_date)}</td>
            <td class="place-col editable-cell" data-edit="place" data-event="${ev.id}" data-value="${ev.place_id || ''}" title="Dvojklik = vybrat místo">${esc(ev.place?.name || '—')}</td>
            <td class="input-cell status-cell">
                <select data-field="status" class="status-${isPending ? 'pending' : 'confirmed'}" title="V jednání = nejde na veřejný web">
                    <option value="pending"${isPending ? ' selected' : ''}>V jednání</option>
                    <option value="confirmed"${!isPending ? ' selected' : ''}>Potvrzeno</option>
                </select>
            </td>
            <td class="input-cell contract-cell">
                <select data-field="contract_signed" class="${ev.contract_signed ? 'yes' : 'no'}">
                    <option value="false"${!ev.contract_signed ? ' selected' : ''}>Ne</option>
                    <option value="true"${ev.contract_signed ? ' selected' : ''}>Ano</option>
                </select>
            </td>
            <td class="input-cell" data-col="cena" style="min-width:90px;"><input type="number" data-field="price_total" value="${ev.price_total ?? 0}" style="min-width:75px; font-size:13px; font-weight:600;"></td>
            <td class="input-cell" data-col="km-apar" style="min-width:65px;"><input type="number" data-field="equipment_km" value="${ev.equipment_km ?? 0}" style="min-width:45px;"></td>
            <td class="input-cell" data-col="apar-najem" style="min-width:80px;"><input type="number" data-field="equipment_rent" value="${ev.equipment_rent ?? 0}" style="min-width:65px;"></td>
            <td class="input-cell" data-col="stravne" style="min-width:70px;"><input type="number" data-field="food_allowance" value="${ev.food_allowance ?? 0}" style="min-width:55px;"></td>
            <td class="input-cell">
                <select data-field="payment_form">
                    <option${(!ev.payment_form || ev.payment_form === 'Hotově (s dokladem)') ? ' selected' : ''}>Hotově (s dokladem)</option>
                    <option${ev.payment_form === 'Faktura' ? ' selected' : ''}>Faktura</option>
                    <option${ev.payment_form === 'Úhrada převodem' ? ' selected' : ''}>Úhrada převodem</option>
                </select>
            </td>
            <td class="musicians-cell">${renderChips(musicians, budget, ev.id)}</td>
            <td class="calc-cell count-cell">${musicians.length}</td>
            <td class="calc-cell num" data-col="mzdy">${fmtKc(budget.totalWages)}</td>
            <td class="calc-cell num">${fmtKc(budget.musiciansTravel)}</td>
            <td class="calc-cell num">${fmtKc(budget.equipmentTransport)}</td>
            <td class="calc-cell num">– ${fmtKc(budget.totalCost)}</td>
            <td class="total-cell num">${fmtKc(budget.bandWage)}</td>
        </tr>
    `;
}

function attachListeners(container) {
    container.querySelectorAll('tbody tr').forEach(row => {
        const eventId = row.dataset.event;
        row.querySelectorAll('[data-field]').forEach(input => {
            input.addEventListener('change', async () => {
                const field = input.dataset.field;
                let value = input.value;
                if (field === 'contract_signed') value = value === 'true';
                else if (['price_total', 'equipment_km', 'equipment_rent', 'food_allowance'].includes(field)) {
                    value = parseFloat(value) || 0;
                }
                const { error } = await supabase.from('events').update({ [field]: value }).eq('id', eventId);
                if (error) {
                    alert('Chyba uložení: ' + error.message);
                    return;
                }
                renderAdministrace(container);
            });
        });
    });

    // Odebrat muzikanta z akce
    container.querySelectorAll('.chip-remove').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const rowId = btn.dataset.row;
            if (!confirm(`Odebrat muzikanta "${btn.dataset.name}" z akce?`)) return;
            const { error } = await supabase.from('event_musicians').delete().eq('id', rowId);
            if (error) { alert('Chyba: ' + error.message); return; }
            renderAdministrace(container);
        });
    });

    // Přidat muzikanta z databáze
    container.querySelectorAll('.muz-chip-add').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            openAddMusicianPopup(btn, btn.dataset.event, () => renderAdministrace(container));
        });
    });

    // Klik na chip (mimo × tlačítko) – otevři popup pro úpravu km/podílu
    container.querySelectorAll('.muz-chip[data-row]').forEach(chip => {
        chip.addEventListener('click', (e) => {
            if (e.target.classList.contains('chip-remove')) return;
            openEditMusicianPopup(chip, () => renderAdministrace(container));
        });
    });

    // Dvojklik na datum / místo – inline editor
    container.querySelectorAll('.editable-cell').forEach(cell => {
        cell.addEventListener('dblclick', () => editCellInline(cell, () => renderAdministrace(container)));
    });
}

async function editCellInline(cell, onSaved) {
    const eventId = cell.dataset.event;
    const kind = cell.dataset.edit;
    const current = cell.dataset.value;

    let editor;
    if (kind === 'date') {
        editor = document.createElement('input');
        editor.type = 'date';
        editor.value = current;
    } else if (kind === 'place') {
        const { data: places } = await supabase.from('places').select('id, name').order('name');
        editor = document.createElement('select');
        editor.innerHTML = '<option value="">— bez místa —</option>' +
            (places || []).map(p => `<option value="${p.id}"${p.id === current ? ' selected' : ''}>${esc(p.name)}</option>`).join('');
    } else {
        return;
    }
    editor.style.cssText = 'width:100%; padding:5px 7px; border:1px solid var(--accent); border-radius:4px; font-family:inherit; font-size:13px;';

    cell.innerHTML = '';
    cell.appendChild(editor);
    editor.focus();
    if (editor.type !== 'select-one') editor.select?.();

    const save = async () => {
        const newVal = editor.value;
        if (newVal !== current && newVal !== '') {
            const field = kind === 'date' ? 'event_date' : 'place_id';
            const { error } = await supabase.from('events').update({ [field]: newVal }).eq('id', eventId);
            if (error) { alert('Chyba uložení: ' + error.message); return; }
        }
        onSaved?.();
    };
    editor.addEventListener('blur', save);
    editor.addEventListener('keydown', (e) => {
        if (e.key === 'Enter')  editor.blur();
        if (e.key === 'Escape') onSaved?.();   // re-render bez uložení
    });
    if (kind === 'place') {
        editor.addEventListener('change', () => editor.blur());
    }
}

function openEditMusicianPopup(anchor, onSaved) {
    document.querySelectorAll('.muz-add-popup').forEach(el => el.remove());
    const rowId = anchor.dataset.row;
    const km = parseInt(anchor.dataset.km, 10) || 0;
    const share = parseInt(anchor.dataset.share, 10) || 100;
    const name = anchor.dataset.name;

    const popup = document.createElement('div');
    popup.className = 'muz-add-popup';
    popup.style.minWidth = '240px';
    popup.innerHTML = `
        <div class="popup-head">Upravit: ${esc(name)}</div>
        <div style="padding: 10px 12px;">
            <div class="spec-row"><label>Km <span class="unit">osobní auto</span></label><input type="number" id="ep-km" value="${km}" min="0" style="width:80px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;text-align:right;"></div>
            <div class="spec-row"><label>Podíl mzdy <span class="unit">%</span></label><input type="number" id="ep-share" value="${share}" min="0" max="200" style="width:80px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;text-align:right;"></div>
            <div style="display:flex; gap:6px; margin-top:8px; justify-content:flex-end;">
                <button class="btn btn-sm" id="ep-cancel">Zrušit</button>
                <button class="btn btn-sm btn-primary" id="ep-save">Uložit</button>
            </div>
        </div>
    `;
    const rect = anchor.getBoundingClientRect();
    popup.style.top = (rect.bottom + window.scrollY + 4) + 'px';
    popup.style.left = (rect.left + window.scrollX) + 'px';
    document.body.appendChild(popup);

    const close = () => popup.remove();
    popup.querySelector('#ep-cancel').onclick = close;
    setTimeout(() => {
        document.addEventListener('click', function once(e) {
            if (!popup.contains(e.target) && !anchor.contains(e.target)) {
                close();
                document.removeEventListener('click', once);
            }
        });
    }, 0);
    popup.querySelector('#ep-save').onclick = async () => {
        const newKm = parseInt(popup.querySelector('#ep-km').value, 10) || 0;
        const newShare = parseInt(popup.querySelector('#ep-share').value, 10) || 100;
        const { error } = await supabase.from('event_musicians')
            .update({ km: newKm, share_percent: newShare })
            .eq('id', rowId);
        close();
        if (error) { alert('Chyba: ' + error.message); return; }
        onSaved?.();
    };
}

async function openAddMusicianPopup(anchor, eventId, onAdded) {
    // Smaž stávající popup
    document.querySelectorAll('.muz-add-popup').forEach(el => el.remove());

    // Načti všechny profily a aktuální muzikanty na akci
    const [{ data: profiles }, { data: existing }] = await Promise.all([
        supabase.from('profiles').select('id, name, musician_role, share_percent').order('name'),
        supabase.from('event_musicians').select('musician_id').eq('event_id', eventId)
    ]);
    const existingIds = new Set((existing || []).map(r => r.musician_id));
    const available = (profiles || []).filter(p => !existingIds.has(p.id));

    if (!available.length) {
        alert('Všichni muzikanti z databáze už jsou na této akci.');
        return;
    }

    const popup = document.createElement('div');
    popup.className = 'muz-add-popup';
    popup.innerHTML = `
        <div class="popup-head">Přidat muzikanta</div>
        <ul>
            ${available.map(p => `
                <li data-id="${p.id}" data-share="${p.share_percent || 100}">
                    ${esc(p.name)}
                    ${p.musician_role === 'stalý host'
                        ? `<span class="host-tag" style="margin-left:6px;">${p.share_percent}%</span>`
                        : ''}
                </li>
            `).join('')}
        </ul>
    `;

    const rect = anchor.getBoundingClientRect();
    popup.style.top = (rect.bottom + window.scrollY + 4) + 'px';
    popup.style.left = (rect.left + window.scrollX) + 'px';
    document.body.appendChild(popup);

    const close = () => popup.remove();
    setTimeout(() => {
        document.addEventListener('click', function once(e) {
            if (!popup.contains(e.target)) {
                close();
                document.removeEventListener('click', once);
            }
        });
    }, 0);

    popup.querySelectorAll('li').forEach(li => {
        li.addEventListener('click', async () => {
            const muzId = li.dataset.id;
            const share = parseInt(li.dataset.share, 10) || 100;
            const { error } = await supabase.from('event_musicians').insert({
                event_id: eventId,
                musician_id: muzId,
                km: 0,
                share_percent: share
            });
            close();
            if (error) { alert('Chyba: ' + error.message); return; }
            onAdded?.();
        });
    });
}

export async function renderAdministrace(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const settings = await getSettings();
    const today = new Date().toISOString().slice(0, 10);

    const { data: events, error } = await supabase
        .from('events')
        .select(`
            *,
            place:places(name),
            blocked_user:profiles!events_blocked_for_fkey(id, name),
            musicians:event_musicians(
                id, musician_id, km, share_percent, wage_override,
                profile:profiles(id, name, musician_role, share_percent)
            )
        `)
        .in('type', ['gig', 'block'])
        .order('event_date');

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(error.message)}</div>`;
        return;
    }

    // Rozděl na akce (gig) a blokované termíny (block)
    const allEvents = events || [];
    const gigs = allEvents.filter((ev) => ev.type !== 'block');
    const blocks = allEvents.filter((ev) => ev.type === 'block');
    const upcoming = gigs.filter((ev) => ev.event_date >= today);
    const past = gigs.filter((ev) => ev.event_date < today).reverse(); // nejnovější první
    const upcomingBlocks = blocks
        .filter((ev) => (ev.event_date_end || ev.event_date) >= today)
        .sort((a, b) => a.event_date.localeCompare(b.event_date));

    if (allEvents.length === 0) {
        container.innerHTML = `
            <div class="admin-toolbar">
                <input class="search-input" type="search" placeholder="Hledat akci...">
                <div class="spacer"></div>
                <button class="btn btn-sm btn-primary" id="admin-new-event-btn">+ Nová akce</button>
            </div>
            <div class="state-banner">Žádné akce.</div>
        `;
        const nb = container.querySelector('#admin-new-event-btn');
        if (nb) nb.addEventListener('click', () => openNewEventModal(() => renderAdministrace(container)));
        return;
    }

    const upcomingCalced = upcoming.map((ev) => calcEvent(ev, settings));
    const pastCalced = past.map((ev) => calcEvent(ev, settings));

    const tableHead = `
        <thead>
            <tr>
                <th>Datum</th>
                <th>Místo</th>
                <th class="input-col">Stav</th>
                <th class="input-col">Smluvně</th>
                <th class="input-col num">Cena</th>
                <th class="input-col num">Km aparatury</th>
                <th class="input-col num">Aparatura (nájem)</th>
                <th class="input-col num">Stravné</th>
                <th class="input-col">Forma platby</th>
                <th>Muzikanti (jméno · km · k vyplacení)</th>
                <th class="calc-col num">Počet</th>
                <th class="calc-col num" data-col="mzdy">Mzdy</th>
                <th class="calc-col num">Cestovné muz.</th>
                <th class="calc-col num" title="Doprava aparatury kapely">Doprava KAP.</th>
                <th class="calc-col num">Náklady celkem</th>
                <th class="total-col num">Kapelnická mzda<br><span style="font-weight:400;font-size:9px;opacity:0.8;">cíl 500 Kč</span></th>
            </tr>
        </thead>
    `;

    const upcomingBody = upcomingCalced.length
        ? upcomingCalced.map(renderRow).join('')
        : '<tr><td colspan="16" style="text-align:center;padding:24px;color:var(--text-muted);">Žádné nadcházející akce.</td></tr>';

    container.innerHTML = `
      <div class="admin-fullbleed">
        <div class="admin-toolbar">
            <input class="search-input" type="search" placeholder="Hledat akci...">
            <div class="spacer"></div>
            <button class="btn btn-sm btn-primary" id="admin-new-event-btn">+ Nová akce</button>
        </div>

        <h3 class="admin-section-title">Nadcházející akce</h3>
        <div class="admin-table-wrap">
            <table class="admin-table">
                ${tableHead}
                <tbody>${upcomingBody}</tbody>
            </table>
        </div>

        <h3 class="admin-section-title">Nadcházející blokované termíny (volno)</h3>
        <div class="admin-blocks">
            ${upcomingBlocks.length
                ? upcomingBlocks.map((b) => `
                    <div class="admin-block-row">
                        <span class="abr-date">${fmtBlockDate(b)}</span>
                        <span class="abr-name">${esc(b.blocked_user?.name || 'Někdo')}</span>
                        <span class="abr-reason">${esc(b.block_reason || 'volno')}</span>
                    </div>`).join('')
                : '<div class="admin-block-empty">Žádné nadcházející blokované termíny.</div>'}
        </div>

        ${past.length > 0 ? `
        <details class="admin-history-section">
            <summary>Historie akcí — proběhlé (${past.length})</summary>
            <div class="admin-table-wrap">
                <table class="admin-table">
                    ${tableHead}
                    <tbody>${pastCalced.map(renderRow).join('')}</tbody>
                </table>
            </div>
        </details>
        ` : ''}

        <div class="footer-note">
            <strong>Živý výpočet:</strong> mění-li se cena, km, pronájem nebo stravné, kapelnická mzda se přepočítá podle vzorce
            <code>per_unit = floor((zbytek − 500) ÷ Σ podíly, 100)</code>.
            Proběhlé akce se automaticky přesunou do sekce <em>Historie akcí</em> — finanční rozpis tam zůstává plně editovatelný.
        </div>
      </div>
    `;

    attachListeners(container);

    const newBtn = container.querySelector('#admin-new-event-btn');
    if (newBtn) newBtn.addEventListener('click', () => {
        openNewEventModal(() => renderAdministrace(container));
    });
}
